import React, { useState } from 'react';
import { FileText, Download, Calendar, Search, X, Map } from 'lucide-react';
import { usePayroll } from '../hooks/usePayroll';
import { useEmployees } from '../hooks/useEmployees';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { calculateForm940Data, formatCurrency } from '../utils/form940Calculator';
import { PDFDocument } from 'pdf-lib';
import Form940Mapper from './Form940Mapper';

interface FieldDiagnostic {
  fieldName: string;
  ourLabel: string;
  attemptedValue: string;
  success: boolean;
  error?: string;
}

const Compliance: React.FC = () => {
  const { payrollEntries } = usePayroll();
  const { employees } = useEmployees();
  const { settings: companySettings } = useCompanySettings();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState<{
    allFields: string[];
    mappedFields: FieldDiagnostic[];
    unmappedFields: string[];
  } | null>(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);
  const [showMapper, setShowMapper] = useState(false);

  const availableYears = Array.from(
    new Set(
      payrollEntries.map(entry => new Date(entry.payPeriod.start).getFullYear())
    )
  ).sort((a, b) => b - a);

  if (availableYears.length === 0) {
    availableYears.push(new Date().getFullYear());
  }

  const form940Data = calculateForm940Data(
    selectedYear,
    payrollEntries,
    employees,
    companySettings?.companyName || 'Company Name',
    companySettings?.companyAddress || 'Company Address',
    '00-0000000'
  );

  const runDiagnostics = async () => {
    setRunningDiagnostic(true);
    setError(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-irs-form?form=f940&year=${selectedYear}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch IRS Form 940');
      }

      const formPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(formPdfBytes);
      const form = pdfDoc.getForm();
      const fields = form.getFields();

      const allFieldNames = fields.map(f => f.getName());
      const mappedFields: FieldDiagnostic[] = [];

      const fieldMappings = [
        {
          fieldName: 'topmostSubform[0].Page1[0].Entity[0].BusinessName_ReadOrder[0].f1_01[0]',
          ourLabel: 'Business Name',
          value: form940Data.businessName,
        },
        {
          fieldName: 'topmostSubform[0].Page1[0].Entity[0].Address[0].f1_03[0]',
          ourLabel: 'Business Address',
          value: form940Data.businessAddress,
        },
        {
          fieldName: 'topmostSubform[0].Page1[0].Entity[0].EIN[0].f1_02[0]',
          ourLabel: 'EIN',
          value: form940Data.ein,
        },
        {
          fieldName: 'topmostSubform[0].Page1[0].LeftCol[0].Line3[0].f1_08[0]',
          ourLabel: 'Total Payments (Line 3)',
          value: formatCurrency(form940Data.totalPayments),
        },
        {
          fieldName: 'topmostSubform[0].Page1[0].LeftCol[0].Line7[0].f1_12[0]',
          ourLabel: 'Total Taxable FUTA Wages (Line 7)',
          value: formatCurrency(form940Data.totalTaxableWages),
        },
        {
          fieldName: 'topmostSubform[0].Page1[0].LeftCol[0].Line8[0].f1_13[0]',
          ourLabel: 'FUTA Tax Before Adjustments (Line 8)',
          value: formatCurrency(form940Data.futaTaxBeforeAdjustments),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].Line12[0].f2_08[0]',
          ourLabel: 'Total FUTA Tax After Adjustments (Line 12)',
          value: formatCurrency(form940Data.futaTaxAfterAdjustments),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].c2_01[0].Pt5-Line16a[0].f2_14[0]',
          ourLabel: 'Quarter 1 (Line 16a)',
          value: formatCurrency(form940Data.quarter1),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].c2_01[0].Pt5-Line16b[0].f2_15[0]',
          ourLabel: 'Quarter 2 (Line 16b)',
          value: formatCurrency(form940Data.quarter2),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].c2_01[0].Pt5-Line16c[0].f2_16[0]',
          ourLabel: 'Quarter 3 (Line 16c)',
          value: formatCurrency(form940Data.quarter3),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].c2_01[0].Pt5-Line16d[0].f2_17[0]',
          ourLabel: 'Quarter 4 (Line 16d)',
          value: formatCurrency(form940Data.quarter4),
        },
        {
          fieldName: 'topmostSubform[0].Page2[0].LeftCol[0].c2_01[0].Pt5-Line16e[0].f2_18[0]',
          ourLabel: 'Total Tax Liability (Line 16e)',
          value: formatCurrency(form940Data.totalTaxLiability),
        },
      ];

      for (const mapping of fieldMappings) {
        try {
          const field = form.getTextField(mapping.fieldName);
          mappedFields.push({
            fieldName: mapping.fieldName,
            ourLabel: mapping.ourLabel,
            attemptedValue: mapping.value,
            success: true,
          });
        } catch (err) {
          mappedFields.push({
            fieldName: mapping.fieldName,
            ourLabel: mapping.ourLabel,
            attemptedValue: mapping.value,
            success: false,
            error: err instanceof Error ? err.message : 'Field not found',
          });
        }
      }

      const mappedFieldNames = fieldMappings.map(m => m.fieldName);
      const unmappedFields = allFieldNames.filter(f => !mappedFieldNames.includes(f));

      setDiagnosticData({
        allFields: allFieldNames,
        mappedFields,
        unmappedFields,
      });
      setShowDiagnostics(true);
    } catch (err) {
      console.error('Error running diagnostics:', err);
      setError(err instanceof Error ? err.message : 'Failed to run diagnostics');
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const handleDownloadForm940 = async () => {
    setGeneratingPDF(true);
    setError(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-irs-form?form=f940&year=${selectedYear}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch IRS Form 940');
      }

      const formPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(formPdfBytes);

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const secondPage = pages[1];

      const { height } = firstPage.getSize();

      const fontSize = 10;
      const einFontSize = 12;

      const drawText = (page: any, text: string, x: number, y: number, size: number = fontSize) => {
        page.drawText(text, {
          x,
          y: height - y,
          size,
        });
      };

      const drawRightAligned = (page: any, text: string, x: number, y: number, size: number = fontSize) => {
        const textWidth = text.length * (size * 0.5);
        page.drawText(text, {
          x: x - textWidth,
          y: height - y,
          size,
        });
      };

      drawText(firstPage, form940Data.businessName, 85, 138, 11);

      const einDigits = form940Data.ein.replace(/-/g, '');
      const einX = 485;
      const einY = 138;
      const einSpacing = 13;
      for (let i = 0; i < einDigits.length; i++) {
        drawText(firstPage, einDigits[i], einX + (i * einSpacing), einY, einFontSize);
      }

      drawText(firstPage, form940Data.businessAddress, 85, 172, 10);

      drawRightAligned(firstPage, formatCurrency(form940Data.totalPayments), 545, 316);

      drawRightAligned(firstPage, formatCurrency(form940Data.totalTaxableWages), 545, 400);

      drawRightAligned(firstPage, formatCurrency(form940Data.futaTaxBeforeAdjustments), 545, 420);

      drawRightAligned(secondPage, formatCurrency(form940Data.futaTaxAfterAdjustments), 545, 197);

      drawRightAligned(secondPage, formatCurrency(form940Data.quarter1), 545, 387);
      drawRightAligned(secondPage, formatCurrency(form940Data.quarter2), 545, 408);
      drawRightAligned(secondPage, formatCurrency(form940Data.quarter3), 545, 428);
      drawRightAligned(secondPage, formatCurrency(form940Data.quarter4), 545, 449);
      drawRightAligned(secondPage, formatCurrency(form940Data.totalTaxLiability), 545, 469);

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-940-${selectedYear}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating Form 940:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate Form 940');
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Tax Compliance</h2>
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Tax Year:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Federal Forms</h3>
        </div>

        <div className="p-6 space-y-6">
          <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Form 940</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Employer's Annual Federal Unemployment (FUTA) Tax Return
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total Payments:</span>
                        <span className="ml-2 font-semibold text-gray-900">
                          ${formatCurrency(form940Data.totalPayments)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Taxable FUTA Wages:</span>
                        <span className="ml-2 font-semibold text-gray-900">
                          ${formatCurrency(form940Data.totalTaxableWages)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">FUTA Tax Due:</span>
                        <span className="ml-2 font-semibold text-green-600">
                          ${formatCurrency(form940Data.futaTaxAfterAdjustments)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Quarterly Breakdown:</p>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-gray-600">Q1</div>
                          <div className="font-semibold">${formatCurrency(form940Data.quarter1)}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-gray-600">Q2</div>
                          <div className="font-semibold">${formatCurrency(form940Data.quarter2)}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-gray-600">Q3</div>
                          <div className="font-semibold">${formatCurrency(form940Data.quarter3)}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-gray-600">Q4</div>
                          <div className="font-semibold">${formatCurrency(form940Data.quarter4)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowMapper(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Map className="h-5 w-5" />
                  <span>Coordinate Mapper</span>
                </button>
                <button
                  onClick={runDiagnostics}
                  disabled={runningDiagnostic}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Search className="h-5 w-5" />
                  <span>{runningDiagnostic ? 'Running...' : 'Diagnose Fields'}</span>
                </button>
                <button
                  onClick={handleDownloadForm940}
                  disabled={generatingPDF}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="h-5 w-5" />
                  <span>{generatingPDF ? 'Generating...' : 'Download PDF'}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-gray-200 rounded-lg">
                <FileText className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-700">Form 941</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Employer's Quarterly Federal Tax Return
                </p>
                <p className="text-xs text-gray-400 mt-2">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">State Forms</h3>
        </div>

        <div className="p-6">
          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-gray-200 rounded-lg">
                <FileText className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-700">State Unemployment Tax</h4>
                <p className="text-sm text-gray-500 mt-1">
                  State-specific unemployment tax forms
                </p>
                <p className="text-xs text-gray-400 mt-2">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Important Information</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Form 940 is due by January 31st for the previous calendar year</li>
                <li>Review all data carefully before submitting to the IRS</li>
                <li>Keep copies of all filed forms for your records</li>
                <li>Consult with a tax professional for accurate filing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {showDiagnostics && diagnosticData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Form 940 Field Diagnostics</h3>
              <button
                onClick={() => setShowDiagnostics(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-auto flex-1 p-6">
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">Total Fields:</span>
                      <span className="ml-2 font-bold text-blue-900">{diagnosticData.allFields.length}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Successfully Mapped:</span>
                      <span className="ml-2 font-bold text-green-900">
                        {diagnosticData.mappedFields.filter(f => f.success).length}
                      </span>
                    </div>
                    <div>
                      <span className="text-red-700">Failed Mapping:</span>
                      <span className="ml-2 font-bold text-red-900">
                        {diagnosticData.mappedFields.filter(f => !f.success).length}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Our Field Mappings</h4>
                  <div className="space-y-2">
                    {diagnosticData.mappedFields.map((field, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-3 ${
                          field.success
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{field.ourLabel}</div>
                            <div className="text-xs font-mono text-gray-600 mt-1 break-all">
                              {field.fieldName}
                            </div>
                            <div className="text-sm text-gray-700 mt-1">
                              Value: <span className="font-semibold">{field.attemptedValue}</span>
                            </div>
                          </div>
                          <div>
                            {field.success ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Success
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Failed
                              </span>
                            )}
                          </div>
                        </div>
                        {!field.success && field.error && (
                          <div className="text-xs text-red-600 mt-2">Error: {field.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    All Available Fields in PDF ({diagnosticData.allFields.length})
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
                    <div className="space-y-1">
                      {diagnosticData.allFields.map((field, index) => (
                        <div
                          key={index}
                          className="text-xs font-mono text-gray-700 break-all py-1 border-b border-gray-200"
                        >
                          {field}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {diagnosticData.unmappedFields.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Unmapped Fields ({diagnosticData.unmappedFields.length})
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      These fields exist in the PDF but we're not currently filling them:
                    </p>
                    <div className="bg-yellow-50 rounded-lg p-4 max-h-96 overflow-auto">
                      <div className="space-y-1">
                        {diagnosticData.unmappedFields.map((field, index) => (
                          <div
                            key={index}
                            className="text-xs font-mono text-gray-700 break-all py-1 border-b border-yellow-200"
                          >
                            {field}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDiagnostics(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showMapper && (
        <Form940Mapper
          onClose={() => setShowMapper(false)}
          selectedYear={selectedYear}
        />
      )}
    </div>
  );
};

export default Compliance;
